import React, { useEffect, useState } from 'react';
import Navbar from '../components/site/Navbar';
import Footer from '../components/site/Footer';
import BookCallModal from '../components/site/BookCallModal';
import ChatWidget from '../components/site/ChatWidget';
import { Button } from '../components/ui/button';
import { CheckCircle2, Sparkles, Loader2, Globe2 } from 'lucide-react';
import { fetchPricing, fetchCurrencies, detectCurrency } from '../lib/api';
import { useI18n } from '../i18n';
import Seo from '../components/Seo';

const PricingPage = () => {
  const { t } = useI18n();
  const [callOpen, setCallOpen] = useState(false);
  const [currency, setCurrency] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    const boot = async () => {
      const list = await fetchCurrencies().catch(() => []);
      setCurrencies(list);
      const stored = localStorage.getItem('hipro_currency');
      const initial = stored || (await detectCurrency());
      setCurrency(initial);
    };
    boot();
  }, []);

  useEffect(() => {
    if (!currency) return;
    setLoading(true);
    fetchPricing(currency)
      .then((res) => {
        setData(res);
        localStorage.setItem('hipro_currency', currency);
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [currency]);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Seo
        title="Pricing & Engagement Models | Hipro Technologies"
        description="Transparent pricing for web development, mobile apps, custom software, AI automation and digital marketing. Fixed-price, time & material, and dedicated team models."
        path="/pricing"
      />
      <Navbar onBookCall={() => setCallOpen(true)} />

      <section className="pt-28 pb-10 bg-slate-950 text-white">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <span className="text-xs font-semibold uppercase tracking-widest text-orange-400">{t('pricing.kicker')}</span>
          <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold">{t('pricing.title')}</h1>
          <p className="mt-4 text-slate-300 max-w-2xl">{t('pricing.body')}</p>

          <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-2 py-1">
            <Globe2 className="h-4 w-4 text-orange-400 ml-2" />
            {currencies.map((c) => (
              <button key={c.code} onClick={() => setCurrency(c.code)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  currency === c.code ? 'bg-orange-500 text-white' : 'text-slate-200 hover:bg-white/10'
                }`}>
                <span className="mr-1">{c.flag}</span>{c.code}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          {loading || !data ? (
            <div className="py-24 text-center text-slate-500"><Loader2 className="h-6 w-6 mx-auto animate-spin" /> {t('common.loading')}</div>
          ) : (
            <>
              <div className="mb-8 text-sm text-slate-500">{t('pricing.pricesShownIn')} <b className="text-slate-900">{data.currency}</b> ({data.symbol}) · {t('pricing.fxNote')}</div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
                {data.packages.map((p) => (
                  <div key={p.id} className={`relative rounded-2xl border p-6 flex flex-col ${p.popular ? 'border-orange-400 shadow-xl bg-white' : 'border-slate-200 bg-white hover:shadow-lg'}`}>
                    {p.popular && (
                      <div className="absolute -top-3 left-6 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                        <Sparkles className="h-3.5 w-3.5" /> {t('pricing.mostPopular')}
                      </div>
                    )}
                    <div>
                      <div className="text-lg font-bold text-slate-900">{p.name}</div>
                      <div className="text-xs text-slate-500 mt-1">{p.tagline}</div>
                    </div>
                    <div className="mt-5">
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-extrabold text-slate-900">{p.price_label}</span>
                        <span className="text-xs text-slate-500">{p.billing}</span>
                      </div>
                      <div className="mt-1 text-xs text-slate-500">{t('pricing.bestFor')} {p.best_for}</div>
                      <div className="mt-1 text-xs text-slate-500">{t('pricing.delivery')} {p.delivery}</div>
                    </div>
                    <ul className="mt-5 space-y-2 text-sm text-slate-700 flex-1">
                      {p.features.map((f) => (
                        <li key={f} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5" /> {f}</li>
                      ))}
                    </ul>
                    <Button onClick={() => setCallOpen(true)} className={`mt-6 h-11 w-full ${p.popular ? 'bg-orange-500 hover:bg-orange-600 text-white' : 'bg-slate-900 hover:bg-slate-800 text-white'}`}>
                      {p.cta}
                    </Button>
                  </div>
                ))}
              </div>
              <p className="mt-8 text-xs text-slate-500">{t('pricing.taxes')}</p>
            </>
          )}
        </div>
      </section>

      <Footer />
      <ChatWidget />
      <BookCallModal open={callOpen} onOpenChange={setCallOpen} />
    </div>
  );
};

export default PricingPage;
